import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api';
import { StorePage } from './Pages';

export default function PageEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  
  
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [templateSuffix, setTemplateSuffix] = useState('page');
  const [isPublished, setIsPublished] = useState(true);

  const { data: page, isLoading } = useQuery({
    queryKey: ['store-page', id],
    queryFn: async () => {
      if (isNew) return null;
      const res: any = await apiClient.get<{ data: StorePage }>(`/merchant/pages/${id}`);
      return res;
    },
    enabled: !isNew,
  });

  useEffect(() => {
    if (page) {
      setTitle(page.title);
      setSlug(page.slug);
      setContent(page.content || '');
      setTemplateSuffix(page.templateSuffix || 'page');
      setIsPublished(page.isPublished);
    }
  }, [page]);

  // Auto-generate slug from title for new pages
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    if (isNew) {
      setSlug(newTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      if (isNew) {
        const res: any = await apiClient.post('/merchant/pages', data);
        return res;
      } else {
        const res: any = await apiClient.patch(`/merchant/pages/${id}`, data);
        return res;
      }
    },
    onSuccess: () => {
      toast('Error saving page');
    },
  });

  const handleSave = () => {
    saveMutation.mutate({
      title,
      slug,
      content,
      templateSuffix,
      isPublished,
    });
  };

  if (isLoading) {
    return <div className="flex justify-center p-12">Loading page...</div>;
  }

  return (
    <div className="flex-1 space-y-6 p-8 pt-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/store/pages')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-3xl font-bold tracking-tight">{isNew ? 'Add Page' : 'Edit Page'}</h2>
        </div>
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          <Save className="mr-2 h-4 w-4" />
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          <div className="space-y-4 rounded-md border p-6 bg-white">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g. About Us"
                value={title}
                onChange={handleTitleChange}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <textarea
                id="content"
                className="flex min-h-[300px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Write your page content here (HTML supported)"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="col-span-1 space-y-6">
          <div className="space-y-4 rounded-md border p-6 bg-white">
            <h3 className="font-semibold text-lg">Visibility</h3>
            <div className="flex items-center justify-between">
              <Label htmlFor="published">Published</Label>
              <Switch
                id="published"
                checked={isPublished}
                onCheckedChange={setIsPublished}
              />
            </div>
          </div>

          <div className="space-y-4 rounded-md border p-6 bg-white">
            <h3 className="font-semibold text-lg">Search engine listing</h3>
            <div className="space-y-2">
              <Label htmlFor="slug">URL Handle</Label>
              <div className="flex">
                <span className="inline-flex items-center rounded-l-md border border-r-0 border-input bg-muted px-3 text-sm text-muted-foreground">
                  /pages/
                </span>
                <Input
                  id="slug"
                  className="rounded-l-none"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-4 rounded-md border p-6 bg-white">
            <h3 className="font-semibold text-lg">Template</h3>
            <p className="text-sm text-muted-foreground">
              Select which template this page will use when rendered by your theme.
            </p>
            <div className="space-y-2">
              <Label htmlFor="templateSuffix">Theme Template</Label>
              <Input
                id="templateSuffix"
                value={templateSuffix}
                onChange={(e) => setTemplateSuffix(e.target.value)}
                placeholder="e.g. page"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
